// IntegrationsBar.tsx — pinned integrations chip row with live connect popups.
// Chip visuals from newproduct-v6.jsx NP6IntegChip.
import React from "react";
import { NPGlyph, NPIcon } from "./atoms";
import type { SourceConnection, SourceId } from "./state";

const CONNECTORS: Array<{ id: SourceId; name: string; glyph: string; noun: string }> = [
    { id: "files", name: "Files", glyph: "files", noun: "files" },
    { id: "jira", name: "Jira", glyph: "jira", noun: "tickets" },
    { id: "azure", name: "Azure DevOps", glyph: "azure", noun: "work items" },
    { id: "github", name: "GitHub", glyph: "github", noun: "repos" },
    { id: "gitlab", name: "GitLab", glyph: "gitlab", noun: "repos" },
    { id: "bitbucket", name: "BitBucket", glyph: "bitbucket", noun: "repos" },
    { id: "azuregit", name: "Azure DevOps Git", glyph: "azure", noun: "repos" }
];

function connectionMeta(id: SourceId, noun: string, c: SourceConnection): string | null {
    if (!c.connected) return null;
    if (id === "files") {
        const n = c.staged?.fileIds?.length ?? c.syncedCount ?? 0;
        return `${n} file${n === 1 ? "" : "s"}`;
    }
    if (c.staged?.repoIds) return `${c.staged.repoIds.length} ${noun}`;
    const name = c.selection?.[0]?.name;
    const count = c.staged?.importedCount ?? c.syncedCount;
    if (name && count != null) return `${name} · ${count} ${noun}`;
    return name ?? "Connected";
}

export function IntegrationsBar({ connections, onOpenSource, disabled, enabledSources, blockedSources }: {
    connections: Record<SourceId, SourceConnection>;
    onOpenSource: (source: SourceId) => void;
    /** Optionally disable the connector row while retaining selected exceptions. */
    disabled?: boolean;
    /** Chip ids that stay clickable even when `disabled` is true. */
    enabledSources?: SourceId[];
    /** Per-chip block reason (e.g. one-tracker rule); blocked chips are disabled with a tooltip. */
    blockedSources?: Partial<Record<SourceId, string>>;
}) {
    return (
        <div style={{
            flex: "0 0 auto", padding: "12px 20px 14px", background: "var(--bg-paper)",
            borderTop: "1px solid var(--divider)", boxShadow: "0 -6px 16px rgba(15,23,42,0.05)"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 650 }}>Integrations</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {CONNECTORS.map(c => {
                    const conn = connections[c.id];
                    const on = conn.connected;
                    const meta = connectionMeta(c.id, c.noun, conn);
                    const blockedReason = blockedSources?.[c.id];
                    const chipDisabled = Boolean(blockedReason) || (disabled && !enabledSources?.includes(c.id));
                    return (
                        <button key={c.id} type="button" aria-label={c.name}
                            disabled={chipDisabled}
                            title={blockedReason || undefined}
                            onClick={chipDisabled ? undefined : () => onOpenSource(c.id)}
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 9,
                                padding: "8px 13px 8px 10px", borderRadius: 11,
                                border: on ? "1px solid rgba(5,150,105,0.45)" : "1px dashed var(--divider)",
                                background: on ? "rgba(5,150,105,0.05)" : "var(--bg-paper)",
                                cursor: chipDisabled ? "default" : "pointer", fontFamily: "inherit", textAlign: "left",
                                opacity: chipDisabled ? 0.55 : 1
                            }}>
                            <NPGlyph name={c.glyph} size={17} />
                            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--fg1)", lineHeight: 1.2 }}>{c.name}</span>
                                {on && meta && (
                                    <span style={{ fontSize: 10.5, color: "#059669", fontWeight: 600 }}>{meta}</span>
                                )}
                            </span>
                            {on
                                ? <span style={{
                                      width: 16, height: 16, borderRadius: 8, background: "#059669",
                                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                                      flex: "0 0 auto"
                                  }}><NPIcon name="check" size={9} sw={3.2} color="#fff" /></span>
                                : <NPIcon name="plus" size={12} sw={2.2} color="var(--fg4)" />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
